import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { FaLock, FaShieldAlt, FaChevronRight } from 'react-icons/fa';
import { api } from '../services/api';
import { useSiteSettings } from '../context/SiteSettingsContext';
import './Checkout.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const toPriceNumber = (value) => typeof value === 'number' ? value : parseInt(String(value || '').replace(/,/g, ''), 10) || 0;
const getEffectivePrice = (product) => {
  const price = toPriceNumber(product?.price);
  const discountPrice = toPriceNumber(product?.discountPrice);
  return discountPrice > 0 && discountPrice < price ? discountPrice : price;
};

const Checkout = () => {
  const { t } = useTranslation();
  const { locale, localePath, formatNumber } = useLocale();
  const { productId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const { settings } = useSiteSettings();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payError, setPayError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.get(`/api/products/${productId}?locale=${encodeURIComponent(locale.code)}`);
        if (res.success && res.data) {
          const p = res.data;
          const rawCardImageUrl = p.cardImageUrl || p.imageUrl || '';
          const cardImageUrl = rawCardImageUrl.startsWith('/') ? `${API_URL}${rawCardImageUrl}` : rawCardImageUrl;
          setProduct({ ...p, cardImageUrl });
        } else {
          setProduct(null);
        }
      } catch (_) {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [locale.code, productId]);

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    setPayError('');
    if (!isAuthenticated) {
      setPayError(t('checkout.authRequired'));
      return;
    }
    if (product?.priceAvailable === false) {
      setPayError(t('products.priceUnavailable'));
      return;
    }
    if (!product?.paymentEnabled) {
      setPayError(t('checkout.marketGatewayUnavailable'));
      return;
    }
    if (!settings.payment.enabled) {
      setPayError(t('checkout.paymentDisabled'));
      return;
    }
    setSubmitting(true);
    try {
      const createRes = await api.post('/api/orders', {
        product: product._id || product.id,
        locale: product.locale || locale.code,
      });
      if (!createRes.success || !createRes.data?._id) {
        setPayError(createRes.message || t('checkout.orderFailed'));
        return;
      }
      const orderId = createRes.data._id;
      const payRes = await api.post(`/api/orders/${orderId}/payment-request`);
      if (payRes.success && payRes.data?.paymentUrl) {
        window.location.href = payRes.data.paymentUrl;
        return;
      }
      setPayError(payRes.message || t('checkout.paymentLinkFailed'));
    } catch (err) {
      setPayError(err.data?.message || err.message || t('checkout.paymentError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="checkout-loader">
          <div className="checkout-loader-spinner" />
          <p>{t('checkout.loading')}</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="checkout-page">
        <div className="checkout-wrap">
          <div className="checkout-not-found-card">
            <h2>{t('checkout.productMissing')}</h2>
            <Link to={localePath('/products')} className="checkout-btn-primary">{t('checkout.productsBack')}</Link>
          </div>
        </div>
      </div>
    );
  }

  const regularPrice = toPriceNumber(product.price);
  const priceAvailable = product.priceAvailable !== false;
  const paymentAvailable = priceAvailable && Boolean(product.paymentEnabled);
  const currencyLabel = product.currencyLabel || locale.currencyLabel;
  const priceNum = getEffectivePrice(product);
  const hasDiscount = priceNum < regularPrice;
  const discountPercent = hasDiscount ? Math.round((1 - priceNum / regularPrice) * 100) : 0;
  const totalDisplay = formatNumber(priceNum);

  return (
    <div className="checkout-page">
      <div className="checkout-wrap">
        <div className="checkout-card">
          {/* هدر کارت */}
          <header className="checkout-header">
            <Link to={localePath(`/products/${product.slug || product._id || product.id}`)} className="checkout-back-link">
              <FaChevronRight /> {t('checkout.productBack')}
            </Link>
            <h1 className="checkout-heading">{t('checkout.title')}</h1>
            <p className="checkout-subheading">{settings.payment.title}</p>
          </header>

          <div className="checkout-grid">
            {/* خلاصه سفارش */}
            <aside className="checkout-summary">
              <div className="checkout-summary-item">
                <div className="checkout-summary-thumb">
                  {product.cardImageUrl ? (
                    <img src={product.cardImageUrl} alt={product.name} />
                  ) : (
                    <span>{product.image || '📦'}</span>
                  )}
                </div>
                <div className="checkout-summary-body">
                  <h3>{product.name}</h3>
                  {product.category && <span className="checkout-summary-cat">{product.category}</span>}
                  <div className="checkout-summary-meta">
                    {hasDiscount ? (
                      <div className="checkout-summary-discount">
                        <del>{formatNumber(regularPrice)} {currencyLabel}</del>
                        <strong>{totalDisplay} {currencyLabel}</strong>
                        <span>{t('checkout.discount', { count: formatNumber(discountPercent) })}</span>
                      </div>
                    ) : (
                      <span>{priceAvailable ? `${formatNumber(1)} × ${totalDisplay} ${currencyLabel}` : t('products.priceUnavailable')}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="checkout-summary-divider" />
              <div className="checkout-summary-total">
                <span>{t('checkout.payable')}</span>
                <strong>{priceAvailable ? `${totalDisplay} ${currencyLabel}` : t('products.priceUnavailable')}</strong>
              </div>
            </aside>

            {/* بخش پرداخت */}
            <main className="checkout-main">
              {!isAuthenticated ? (
                <div className="checkout-gate">
                  <div className="checkout-gate-icon"><FaLock /></div>
                  <h2>{t('checkout.loginTitle')}</h2>
                  <p>{t('checkout.loginHint')}</p>
                  <Link
                    to={localePath('/login')}
                    state={{ from: localePath(`/checkout/${productId}`) }}
                    className="checkout-btn-primary checkout-btn-full"
                  >
                    {t('checkout.login')}
                  </Link>
                  <p className="checkout-gate-footer">
                    {settings.status.registrationEnabled ? <>{t('checkout.noAccount')} <Link to={localePath('/register')}>{t('checkout.register')}</Link></> : t('auth.registrationDisabled')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmitPayment} className="checkout-form">
                  <div className="checkout-form-user">
                    <span>{t('checkout.payingAs')}</span>
                    <strong>{user?.name || user?.email}</strong>
                  </div>
                  <div className="checkout-secure-badge">
                    <FaLock />
                    <span>{paymentAvailable ? t('checkout.secure') : t('checkout.marketGatewayUnavailable')}</span>
                  </div>
                  {payError && <p className="checkout-form-error">{payError}</p>}
                  <button type="submit" className="checkout-btn-pay" disabled={submitting || !settings.payment.enabled || !paymentAvailable}>
                    <span className="checkout-btn-pay-label">{!paymentAvailable ? t('checkout.marketGatewayUnavailable') : !settings.payment.enabled ? t('checkout.gatewayDisabled') : (submitting ? t('checkout.submitting') : settings.payment.title)}</span>
                    <span className="checkout-btn-pay-amount">{priceAvailable ? `${totalDisplay} ${currencyLabel}` : '—'}</span>
                  </button>
                  <p className="checkout-form-trust">
                    <FaShieldAlt /> {t('checkout.trust')}
                  </p>
                </form>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
